import { useEffect, useState } from "react";
import {
  Filter,
  FilterValue,
  InputFilter,
  InputType,
} from "../../types/filter";
import { TrackingProps } from "../../types/component";
import { useTranslation } from "@coworker/locales";
import { FixaModal } from "../../../wrappers/FixaModal/FixaModal";
import { FixaSheets } from "../../../wrappers/FixaModal/FixaSheets";
import { FixaModalFooter } from "../../../wrappers/FixaModal/FixaModalFooter";
import FixaButton from "../../../wrappers/FixaButton/FixaButton";
import { FixaModalHeader } from "../../../wrappers/FixaModal/FixaModalHeader";
import { FixaModalBody } from "../../../wrappers/FixaModal/FixaModalBody";
import { FixaAccordion } from "../../../wrappers/FixaAccordion/FixaAccordion";
import {
  filterValuesAreTheSame,
  removeFilterValueFromValues,
  updatedFilterValueFromValues,
} from "../../utils/filter/filtervalue";
import FilterModalCss from "./FilterModal.module.css";
import { InputSelectOption } from "./InputSelectOption";
import { InputMultiSelectOption } from "./InputMultiSelectOption";
import { InputGroupOption } from "./InputGroupOption";
import { InputCheckboxOption } from "./InputCheckboxOption";
import trackerHelper from "@coworker/app/src/helpers/tracker";
import { useChangedFiltersCount } from "../../utils/useChangedFiltersCount";

// useFiltersFactory is a hook that returns/runs the filter hooks. This makes
// it possible to reuse the same filters with different filterValues/states.
// This is used to let the filter modal have its own state and only apply that
// to the state used when filtering data when a user presses apply button.
interface FilterModalProps {
  visible: boolean;
  scrollTo?: string;
  filterValues: FilterValue[];
  useFiltersFactory: (
    filterValues: FilterValue[],
    setFilterValues: (values: FilterValue[]) => void
  ) => Filter[];
  setFilterValues: (filterValues: FilterValue[]) => void;
  onClose: () => void;
  newFilterValue?: FilterValue | null;
  tracking: TrackingProps;
}

export const FilterModal = ({
  visible,
  scrollTo,
  useFiltersFactory,
  filterValues,
  setFilterValues,
  onClose,
  newFilterValue,
  tracking,
}: FilterModalProps) => {
  const { t } = useTranslation();
  const [savedValues, setSavedValues] = useState<FilterValue[]>(filterValues);
  const [openFilters, setOpenFilters] = useState<string[]>([]);
  const filters = useFiltersFactory(savedValues, setSavedValues);
  const changedFilterCount = useChangedFiltersCount(filters, savedValues);

  useEffect(() => {
    if (newFilterValue) {
      const updatedFilterValues = removeFilterValueFromValues(
        [...filterValues],
        newFilterValue.filterId
      );
      updatedFilterValues.push(newFilterValue);
      setSavedValues(updatedFilterValues);
    } else {
      setSavedValues(filterValues);
    }
  }, [filterValues, visible, setSavedValues, newFilterValue]);

  useEffect(() => {
    if (scrollTo && visible) {
      setOpenFilters([scrollTo]);
    } else {
      setOpenFilters([]);
    }
  }, [scrollTo, visible]);

  const scrollIntoView = (scrollTo: string) => {
    const element = document.getElementById(`filter-accordion-${scrollTo}`);
    element?.scrollIntoView({
      behavior: "instant",
    });
  };

  const onModalOpened = () => {
    if (scrollTo) {
      scrollIntoView(scrollTo);
    }
  };

  const handleOnValueChange = (filter: InputFilter, value: FilterValue) => {
    setSavedValues(updatedFilterValueFromValues(savedValues, filter, value));
    const { dependentOfFilterId } = value;
    if (dependentOfFilterId) {
      const filters = openFilters.filter(
        (filterId) => filterId !== dependentOfFilterId
      );
      filters.push(dependentOfFilterId);
      setOpenFilters(filters);
      setTimeout(() => {
        scrollIntoView(dependentOfFilterId);
      }, 0);
    }
  };

  const handleOnOpenChange = (filter: Filter, isOpen: boolean) => {
    const filters = openFilters.filter((filterId) => filterId !== filter.id);
    if (isOpen) {
      filters.push(filter.id);
    }
    setOpenFilters(filters);
  };

  return (
    <FixaModal
      visible={visible}
      handleCloseBtn={onClose}
      className={FilterModalCss["filters-modal"] ?? ""}
      onModalOpened={onModalOpened}
    >
      <FixaSheets
        alignment={"right"}
        preserveAlignment={false}
        size="small"
        fullSize={true}
        header={
          <FixaModalHeader
            title={t("filtersString")}
            floating={false}
            closeBtnClick={onClose}
          />
        }
        footer={
          <FixaModalFooter>
            {savedValues.length > 0 && (
              <FixaButton
                text={t("resetString")}
                type="secondary"
                onClick={() => {
                  trackerHelper.filter.trackFilterGeneralClicked(
                    tracking.feature,
                    tracking.mode,
                    "modal_reset_button"
                  );
                  setOpenFilters([]);
                  setSavedValues([]);
                  setFilterValues([]);
                }}
              />
            )}
            <FixaButton
              text={t("applyString2", { count: changedFilterCount })}
              type="primary"
              onClick={() => {
                trackerHelper.filter.trackFilterGeneralClicked(
                  tracking.feature,
                  tracking.mode,
                  "modal_apply_button"
                );
                if (!filterValuesAreTheSame(savedValues, filterValues)) {
                  setFilterValues(savedValues);
                  setOpenFilters([]);
                  setSavedValues([]);
                }
                onClose();
              }}
            />
          </FixaModalFooter>
        }
      >
        <FixaModalBody>
          <FixaAccordion collapsible={false} padded={false}>
            {filters
              .filter(({ enabled }) => enabled)
              // Put a single grouped filter in the main filter list
              .map((filter) => {
                if (filter.inputType === InputType.Group) {
                  const enabledGroupFilters = filter.filters.filter(
                    ({ enabled }) => enabled
                  );
                  if (enabledGroupFilters.length === 1) {
                    return enabledGroupFilters[0]!;
                  }
                }
                return filter;
              })
              .map((filter) => {
                switch (filter.inputType) {
                  case InputType.Select: {
                    return (
                      <InputSelectOption
                        key={filter.id}
                        filter={filter}
                        filterValues={savedValues}
                        openFilters={openFilters}
                        onChange={(value) => handleOnValueChange(filter, value)}
                        onOpenChange={(isOpen) =>
                          handleOnOpenChange(filter, isOpen)
                        }
                      />
                    );
                  }
                  case InputType.MultiSelect: {
                    return (
                      <InputMultiSelectOption
                        key={filter.id}
                        filter={filter}
                        filterValues={savedValues}
                        openFilters={openFilters}
                        onChange={(value) => handleOnValueChange(filter, value)}
                        onOpenChange={(isOpen) =>
                          handleOnOpenChange(filter, isOpen)
                        }
                      />
                    );
                  }
                  case InputType.Group: {
                    return (
                      <InputGroupOption
                        key={filter.id}
                        filter={filter}
                        filterValues={savedValues}
                        openFilters={openFilters}
                        setFilterValues={(values) => setSavedValues(values)}
                        onOpenChange={(isOpen) =>
                          handleOnOpenChange(filter, isOpen)
                        }
                      />
                    );
                  }
                  case InputType.Checkbox: {
                    return (
                      <InputCheckboxOption
                        key={filter.id}
                        filter={filter}
                        filterValues={savedValues}
                        openFilters={openFilters}
                        onOpenChange={(isOpen) =>
                          handleOnOpenChange(filter, isOpen)
                        }
                        setFilterValues={setSavedValues}
                      />
                    );
                  }
                  default:
                    return null;
                }
              })}
          </FixaAccordion>
        </FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
};
